import React, { useState } from "react";
import { ChevronDown, Lock } from "lucide-react";

const CATS = [
  { key: "cash_eligible", label: "Cash-eligible earned credits", tone: "text-[#D4AF37]",
    blurb: "Earned credits that currently meet DateBid's cash-eligibility rules. This is informational only: any future redemption would still follow the redemption policy and any review or hold." },
  { key: "pending", label: "Pending rewards", tone: "text-[#E5A84B]",
    blurb: "New rewards waiting out the hold period. Pending does not mean approved for cash redemption." },
  { key: "under_review", label: "Under review", tone: "text-[#E58CA0]",
    blurb: "The payment behind these rewards was refunded or disputed. They stay in your balance and are spendable, but are not cash-eligible while under review." },
  { key: "spendable_only", label: "Spendable-only credits", tone: "text-[#6FD79E]",
    blurb: "Credits you can spend on DateBid that are not eligible for cash redemption, for example the share of a reward paid for with promotional credits." },
  { key: "unverified_earlier", label: "Unverified earlier credits", tone: "text-[#B5AEA4]",
    blurb: "Older credits whose funding source can't be verified. They remain spendable but don't qualify for cash redemption unless their eligibility is legitimately established." },
];

export function CashNotice({ text }) {
  return (
    <div data-testid="earnings-cash-notice" role="alert"
      className="flex items-start gap-3 rounded-2xl border border-[#E5A84B]/50 bg-[#E5A84B]/10 p-4 text-sm text-[#F3C77A]">
      <Lock size={18} className="mt-0.5 shrink-0" /><span>{text}</span>
    </div>
  );
}

function Item({ it }) {
  return (
    <li className="rounded-xl bg-black/20 border border-white/5 p-3 text-xs" data-testid={`explainer-item-${it.lot_id}`}>
      <div className="flex justify-between text-[#D8D2C9]">
        <span>{it.reward_type}{it.earned_on ? ` · ${it.earned_on.slice(0, 10)}` : ""}</span>
        <span className="font-medium">{it.credits} credits</span>
      </div>
      <p className="text-[#9E978E] mt-1 leading-relaxed" data-testid={`explainer-reason-${it.lot_id}`}>{it.reason}</p>
    </li>
  );
}

function Category({ c, data }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-4 border-t border-white/8 first:border-t-0" data-testid={`explainer-cat-${c.key}`}>
      <button onClick={() => setOpen(!open)} disabled={!data.count} data-testid={`explainer-toggle-${c.key}`}
        className="w-full flex items-start justify-between gap-4 text-left">
        <div>
          <div className="text-sm text-[#FBF8F3]">{c.label}</div>
          <p className="text-xs text-[#B5AEA4] leading-snug mt-1 max-w-xl">{c.blurb}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`font-serif text-2xl ${c.tone}`} data-testid={`explainer-total-${c.key}`}>{data.total}</span>
          {data.count > 0 && <ChevronDown size={16} className={`text-[#9E978E] transition-transform ${open ? "rotate-180" : ""}`} />}
        </div>
      </button>
      {open && <ul className="mt-3 space-y-2">{data.items.map((it) => <Item key={it.lot_id} it={it} />)}</ul>}
    </div>
  );
}

export function CategoryList({ x }) {
  return (
    <div className="db-glass rounded-3xl border border-[#D4AF37]/20 p-6" data-testid="explainer-categories">
      <h2 className="font-serif text-xl text-[#FBF8F3] mb-1">What your earned credits mean</h2>
      <p className="text-xs text-[#9E978E] mb-2">Tap a category to see why each credit is counted there, based on your own credit records.</p>
      {CATS.map((c) => <Category key={c.key} c={c} data={x.categories[c.key]} />)}
    </div>
  );
}

export function Examples({ x }) {
  return (
    <div className="db-glass rounded-3xl border border-[#D4AF37]/20 p-6" data-testid="explainer-examples">
      <h2 className="font-serif text-xl text-[#FBF8F3] mb-1">How the current rule works</h2>
      <p className="text-xs text-[#B5AEA4] mb-4" data-testid="explainer-rule">{x.rule}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {x.examples.map((e, i) => (
          <div key={i} className="rounded-2xl bg-black/20 border border-white/5 p-4" data-testid={`explainer-example-${i}`}>
            <div className="text-sm text-[#FBF8F3]">{e.title}</div>
            <div className="text-[11px] text-[#9E978E] mt-1">Price {e.price} · Reward {e.reward} · {e.funding}</div>
            <div className="text-xs text-[#D4AF37] mt-2" data-testid={`explainer-example-eligible-${i}`}>Cash-eligible: {e.cash_eligible}</div>
            <p className="text-xs text-[#B5AEA4] mt-1 leading-relaxed">{e.explain}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
